// frontend/dashboard/src/composables/useAuth/useAuth.js
// Responsible for: auth state management — localStorage token, login, logout, register
//
// English: Singleton composable managing auth_token in localStorage.
//          Exposes reactive isAuthenticated, user, and auth actions (login, logout, register, fetchMe).
//          Called by main.js on boot to hydrate apiClient + wsClient from stored token.
//
// Thai: composable singleton สำหรับจัดการ auth_token ใน localStorage
//       expose isAuthenticated, user แบบ reactive และ actions (login, logout, register, fetchMe)
//       ถูกเรียกจาก main.js ตอนเปิดแอปเพื่อ hydrate apiClient + wsClient จาก token ที่เก็บไว้

'use strict';

import { ref, readonly } from 'vue';
import apiClient from '../../services/apiClient.js';
import { debugLog, debugWarn } from '../../utils/debugGate.js';

const AUTH_TOKEN_KEY = 'auth_token';

// ---- Singleton reactive state -----------------------------------------------

const isAuthenticated = ref(false);
const user            = ref(null);
const authError       = ref(null);
const isLoading       = ref(false);   // true only during login() / register() submit
const isHydrating     = ref(false);   // true only during hydrate() — must NOT block login form UI
const isHydrated      = ref(false);

// English: Prevents concurrent fetchMe calls from racing and double-writing reactive state.
//          A second call that arrives while one is already in flight returns immediately
//          without making a network request or touching user / isAuthenticated.
// Thai: ป้องกัน fetchMe ที่ถูกเรียกพร้อมกันจาก race กันและ write reactive state ซ้ำซ้อน
//       call ที่สองที่เข้ามาขณะมี call อยู่ระหว่างดำเนินการจะ return ทันที
//       โดยไม่ทำ network request และไม่แตะ user / isAuthenticated
let fetchMeInFlight = false;
let hydrateInFlight = null;
let hasHydrateBeenRequested = false;

// ---- localStorage helpers ---------------------------------------------------

function readStoredToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) || null;
  } catch (_) {
    return null;
  }
}

function writeStoredToken(token) {
  try {
    if (token) {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch (_) { /* storage may be unavailable */ }
}

// ---- Apply token to HTTP + WS clients ---------------------------------------

function applyToken(token) {
  apiClient.setAuthToken(token || null);

  // English: wsClient singleton may not be imported here to avoid circular deps.
  //          Token for WS is applied lazily by useWsDashboardClient when connecting.
  //          The WS composable reads localStorage directly on connect.
  // Thai: ไม่ import wsClient ที่นี่เพื่อป้องกัน circular deps
  //       token สำหรับ WS จะถูก apply แบบ lazy โดย useWsDashboardClient เมื่อ connect
  //       composable ของ WS อ่าน localStorage โดยตรงเมื่อ connect
}

function commitAuthenticatedSession(token, nextUser, options = {}) {
  const {
    hydrated = true,
  } = options;

  writeStoredToken(token || null);
  applyToken(token || null);
  user.value            = nextUser || null;
  isAuthenticated.value = Boolean(token);
  authError.value       = null;
  isHydrated.value      = hydrated;
}

function clearAuthState(options = {}) {
  const {
    hydrated = true,
    preserveError = false,
  } = options;

  writeStoredToken(null);
  applyToken(null);
  user.value            = null;
  isAuthenticated.value = false;
  if (!preserveError) {
    authError.value = null;
  }
  isHydrated.value = hydrated;
}

// ---- Reactive write helpers -------------------------------------------------

// English: Returns true when both user objects represent the same identity.
//          Compares only the four stable identity fields — NOT a full deep equality check.
//          Used by fetchMe to avoid replacing user.value with a new object that carries
//          the same identity, which would fire downstream Vue watchers unnecessarily.
//          Fields chosen: user_id (primary key), tenant_id (scope), role (permissions),
//          email (display). Any real identity change on the backend will differ on at
//          least one of these fields.
// Thai: คืน true เมื่อ user object ทั้งสองแทน identity เดียวกัน
//       เปรียบเทียบเฉพาะ 4 identity fields ที่ stable — ไม่ใช่ deep equality แบบเต็ม
//       ใช้โดย fetchMe เพื่อไม่ให้ user.value ถูกแทนที่ด้วย object ใหม่ที่มี identity เดิม
//       ซึ่งจะ fire Vue watchers ที่ downstream โดยไม่จำเป็น
//       fields ที่เลือก: user_id (primary key), tenant_id (scope), role (permissions),
//       email (display) — identity ที่เปลี่ยนจริงบน backend จะต่างกันอย่างน้อย 1 field
function isSameUser(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.user_id   === b.user_id   &&
    a.tenant_id === b.tenant_id &&
    a.role      === b.role      &&
    a.email     === b.email
  );
}

function readErrorStatus(err) {
  return err?.context?.status || err?.response?.status || 0;
}

// ---- Public composable -------------------------------------------------------

export function useAuth() {

  /**
   * hydrate
   * English: Called on app startup — restores token from localStorage into apiClient,
   *          then calls /api/auth/me to populate user.value with real profile data.
   *          isAuthenticated is set synchronously (so router guards work immediately).
   *          user.value is populated asynchronously (Vue reactivity updates the UI).
   *          If /api/auth/me fails → token is cleared and user falls back to guest.
   *          main.js should await hydrate() before mounting to avoid flash of empty user state.
   * Thai: เรียกตอนเปิดแอป — คืน token จาก localStorage เข้า apiClient
   *       แล้วเรียก /api/auth/me เพื่อ populate user.value ด้วยข้อมูล profile จริง
   *       isAuthenticated ถูกตั้งค่าแบบ synchronous (เพื่อให้ router guards ทำงานได้ทันที)
   *       user.value ถูก populate แบบ asynchronous (Vue reactivity อัปเดต UI เอง)
   *       ถ้า /api/auth/me ล้มเหลว → ล้าง token และ fallback เป็น guest
   *       main.js ควร await hydrate() ก่อน mount เพื่อป้องกัน flash ของ user state ว่างเปล่า
   */
  async function hydrate() {
    if (hydrateInFlight) {
      return hydrateInFlight;
    }

    hydrateInFlight = (async () => {
      // English: Use isHydrating, NOT isLoading — hydration runs automatically on boot
      //          and must not disable the login submit button before the user does anything.
      // ไทย: ใช้ isHydrating ไม่ใช่ isLoading — hydration รันอัตโนมัติตอนเปิดแอป
      //      และต้องไม่ disable ปุ่ม Submit ของ login form ก่อนที่ผู้ใช้จะกดอะไร
      isHydrating.value = true;
      hasHydrateBeenRequested = true;

      const token = readStoredToken();

      if (!token) {
        clearAuthState({ hydrated: true });

        debugLog('[useAuth][debug]', {
          action:    'hydrate',
          has_token: false,
          message_en: 'No stored auth token found. User is unauthenticated on boot.',
          message_th: 'ไม่พบ auth token ที่เก็บไว้ ผู้ใช้เป็น unauthenticated ตอนเปิดแอป',
        });
        return false;
      }

      // Step 1 — restore token into apiClient synchronously
      // so the following /api/auth/me call is authorized
      applyToken(token);
      isAuthenticated.value = true;
      authError.value = null;

      debugLog('[useAuth][debug]', {
        action:    'hydrate_token_restored',
        has_token: true,
        message_en: 'Auth token restored from localStorage. Fetching user profile from /api/auth/me.',
        message_th: 'คืนค่า auth token จาก localStorage แล้ว กำลังดึงข้อมูลผู้ใช้จาก /api/auth/me',
      });

      // Step 2 — call /api/auth/me to load real user data into reactive state
      try {
        const res  = await apiClient.get('/api/auth/me');
        const data = res.data || res;

        commitAuthenticatedSession(token, data.user || null, { hydrated: true });

        debugLog('[useAuth][debug]', {
          action:    'hydrate_user',
          has_token: true,
          user_id:   data.user?.user_id,
          tenant_id: data.user?.tenant_id,
          role:      data.user?.role,
          message_en: 'User profile loaded after token hydration.',
          message_th: 'โหลดข้อมูลผู้ใช้หลัง restore token สำเร็จ',
        });

        return true;
      } catch (err) {
        const status = readErrorStatus(err);

        debugWarn('[useAuth][warn]', {
          action:    'hydrate_me_failed',
          has_token: true,
          status,
          error:     err?.message || 'unknown',
          message_en: 'Failed to load user profile after token hydration — clearing token and falling back to guest.',
          message_th: 'โหลดข้อมูลผู้ใช้หลัง restore token ล้มเหลว — ล้าง token และกลับสู่ guest mode',
        });

        // Clear bad/expired token — do NOT leave user in an ambiguous half-authenticated state
        clearAuthState({ hydrated: true });

        return false;
      } finally {
        isHydrating.value = false;
        hydrateInFlight = null;
      }
    })();

    return hydrateInFlight;
  }

  /**
   * login
   * @param {string} email
   * @param {string} password
   * @returns {object} { ok, user, error? }
   */
  async function login(email, password) {
    isLoading.value = true;
    authError.value = null;

    try {
      const res = await apiClient.post('/api/auth/login', { email, password });
      const data = res.data || res;

      if (!data.token) {
        throw new Error('Server did not return a token');
      }

      commitAuthenticatedSession(data.token, data.user || null, { hydrated: true });

      debugLog('[useAuth][debug]', {
        action:    'login_success',
        user_id:   data.user?.user_id,
        tenant_id: data.user?.tenant_id,
        message_en: 'Login succeeded. Token stored in localStorage.',
        message_th:  'Login สำเร็จ เก็บ token ลง localStorage แล้ว',
      });

      return { ok: true, user: data.user };
    } catch (err) {
      const message = err?.parsed?.error?.message || err?.message || 'Login failed';

      clearAuthState({ hydrated: true, preserveError: true });
      authError.value = message;

      debugWarn('[useAuth][warn]', {
        action:     'login_error',
        message:    message,
        message_en: 'Login failed. No token stored.',
        message_th:  'Login ล้มเหลว ไม่มีการเก็บ token',
      });

      return { ok: false, error: message };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * register
   * English: Sends registration request. Backend auto-generates tenant_id from companyName.
   *          Client must NOT supply tenantId — removed from this interface.
   * Thai: ส่งคำขอ register — backend สร้าง tenant_id อัตโนมัติจาก companyName
   *       Client ห้ามส่ง tenantId — ลบออกจาก interface แล้ว
   * @param {string} email
   * @param {string} password
   * @param {string} companyName  - used to generate tenant_id on backend
   * @param {string} [displayName] - optional display name
   * @returns {object} { ok, user, company, site, error? }
   */
  async function register(email, password, companyName, displayName) {
    isLoading.value = true;
    authError.value = null;

    try {
      const res = await apiClient.post('/api/auth/register', {
        email,
        password,
        company_name:  companyName,
        display_name:  displayName || undefined,
      });
      const data = res.data || res;

      if (!data.token) {
        throw new Error('Server did not return a token');
      }

      commitAuthenticatedSession(data.token, data.user || null, { hydrated: true });

      debugLog('[useAuth][debug]', {
        action:    'register_success',
        user_id:   data.user?.user_id,
        tenant_id: data.user?.tenant_id,
        message_en: 'Registration succeeded. Token stored in localStorage.',
        message_th:  'Registration สำเร็จ เก็บ token ลง localStorage แล้ว',
      });

      return { ok: true, user: data.user, company: data.company, site: data.site };
    } catch (err) {
      const message = err?.parsed?.error?.message || err?.message || 'Registration failed';

      clearAuthState({ hydrated: true, preserveError: true });
      authError.value = message;

      debugWarn('[useAuth][warn]', {
        action:     'register_error',
        message:    message,
        message_en: 'Registration failed. No token stored.',
        message_th:  'Registration ล้มเหลว ไม่มีการเก็บ token',
      });

      return { ok: false, error: message };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * logout
   * English: Clears localStorage token and resets client auth state.
   * Thai: ลบ token ออกจาก localStorage และ reset auth state ของ client
   */
  function logout() {
    clearAuthState({ hydrated: true });

    debugLog('[useAuth][debug]', {
      action:     'logout',
      message_en: 'User logged out. Token removed from localStorage.',
      message_th:  'ผู้ใช้ logout แล้ว ลบ token ออกจาก localStorage',
    });
  }

  /**
   * fetchMe
   * English: Fetches current user profile from /api/auth/me using stored token.
   *          Guards against concurrent calls and avoids redundant reactive writes:
   *          - user.value is only replaced when identity fields actually changed.
   *          - isAuthenticated.value is only set when transitioning false → true.
   *          Both guards prevent downstream Vue watchers from firing on no-op refreshes.
   * Thai: ดึงข้อมูลผู้ใช้ปัจจุบันจาก /api/auth/me โดยใช้ token ที่เก็บไว้
   *       ป้องกัน concurrent calls และหลีกเลี่ยง reactive writes ที่ไม่จำเป็น:
   *       - user.value ถูกแทนที่เฉพาะเมื่อ identity fields เปลี่ยนจริง
   *       - isAuthenticated.value ถูก set เฉพาะเมื่อเปลี่ยนจาก false → true
   *       ทั้งสอง guard ป้องกัน Vue watchers ที่ downstream จาก fire บน refresh ที่ไม่เปลี่ยนอะไร
   */
  async function fetchMe() {
    // English: Drop concurrent calls immediately — do not touch reactive state.
    // Thai: drop concurrent calls ทันที — ไม่แตะ reactive state
    if (fetchMeInFlight) {
      debugLog('[useAuth][debug]', {
        action:     'fetchMe_skipped',
        message_en: 'fetchMe skipped — a call is already in flight. Prevents race and double reactive write.',
        message_th: 'ข้าม fetchMe — มี call อยู่ระหว่างดำเนินการ ป้องกัน race และการ write reactive state ซ้ำ',
      });
      return { ok: false, error: 'fetchMe already in flight' };
    }

    const token = readStoredToken();

    if (!token) {
      clearAuthState({ hydrated: true });
      return { ok: false, error: 'No stored token' };
    }

    fetchMeInFlight = true;

    try {
      const res  = await apiClient.get('/api/auth/me');
      const data = res.data || res;

      const incoming = data.user || null;
      const tokenForSession = readStoredToken();

      // English: If the token disappeared while fetchMe was in flight, treat the session as guest.
      // Thai: ถ้า token ถูกลบไประหว่างที่ fetchMe ยังทำงาน ให้ถือว่า session กลับเป็น guest
      if (!tokenForSession) {
        clearAuthState({ hydrated: true });
        return { ok: false, error: 'No stored token' };
      }

      // English: Only write user.value when identity fields actually changed.
      //          A fresh object from the API with the same user_id / tenant_id / role / email
      //          must not trigger downstream Vue watchers (App.vue routeTraceSnapshot, auth watcher).
      // Thai: write user.value เฉพาะเมื่อ identity fields เปลี่ยนจริง
      //       object ใหม่จาก API ที่มี user_id / tenant_id / role / email เดิม
      //       ต้องไม่ trigger Vue watchers ที่ downstream (App.vue routeTraceSnapshot, auth watcher)
      if (!isSameUser(user.value, incoming)) {
        user.value = incoming;
      }

      if (!isAuthenticated.value) {
        isAuthenticated.value = true;
      }
      authError.value = null;
      if (!isHydrated.value) {
        isHydrated.value = true;
      }

      return { ok: true, user: incoming };
    } catch (err) {
      const status = readErrorStatus(err);

      if (status === 401 || status === 403) {
        clearAuthState({ hydrated: true });
      } else if (!isHydrated.value) {
        isHydrated.value = true;
      }

      return { ok: false, error: err?.message || 'fetchMe failed' };
    } finally {
      fetchMeInFlight = false;
    }
  }

  /**
   * getToken
   * English: Returns stored JWT for use in WS connections or direct API calls.
   * Thai: คืนค่า JWT ที่เก็บไว้ สำหรับใช้ใน WS connections หรือ API calls โดยตรง
   */
  function getToken() {
    return readStoredToken();
  }

  /**
 * getSessionSnapshot
 * English: Returns a lightweight snapshot of current auth/session state
 * Thai: คืน snapshot ของ session ปัจจุบัน ใช้ debug / guard / diagnostics
 */
  function getSessionSnapshot() {
    const token = readStoredToken();

    return {
      token,
      hasToken: Boolean(token),
      isAuthenticated: isAuthenticated.value,
      isHydrated: isHydrated.value,
      authError: authError.value,
      isLoading: isLoading.value,
      isHydrating: isHydrating.value,
      user: user.value,
      userRole: user.value?.role || null,
      tenant_id: user.value?.tenant_id || null,
      user_id: user.value?.user_id || user.value?.id || null,
    };
  }

  if (!hasHydrateBeenRequested && !isHydrated.value && !hydrateInFlight) {
    hydrate().catch((err) => {
      debugWarn('[useAuth][warn]', {
        action: 'hydrate_auto_start_failed',
        error: err?.message || 'unknown',
        message_en: 'Automatic hydrate start failed unexpectedly.',
        message_th: 'การเริ่ม hydrate อัตโนมัติล้มเหลวโดยไม่คาดคิด',
      });

      clearAuthState({ hydrated: true, preserveError: true });
      authError.value = err?.message || 'Automatic hydrate failed';
      isLoading.value = false;
    });
  }

  return {
    // state (readonly externally)
    isAuthenticated: readonly(isAuthenticated),
    user:            readonly(user),
    authError:       readonly(authError),
    isLoading:       readonly(isLoading),
    isHydrating:     readonly(isHydrating),
    isHydrated:      readonly(isHydrated),

    // actions
    hydrate,
    login,
    logout,
    register,
    fetchMe,
    getToken,
    getSessionSnapshot,
  };
}